import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SCOPE_MODE, TESTSTUFE, canInAnyScope } from '@testflow/shared';
import { type ScopeMode, type Teststufe, chainOrderEnforced } from '@testflow/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { ErrorBoundary } from '../../components/error-boundary.js';
import { toDateInputValue, toIsoOrNull } from '../../lib/date-input.js';
import { mapApiError } from '../../lib/errors.js';
import { type TestPlanUpdateBody, parsePlanEvidence, plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { PlanProgressSection } from './plan-progress.js';
import { PlanSignoffStatus } from './plan-signoff-status.js';
import { PlanStatusActions, PlanStatusChip } from './plan-status-actions.js';
import { PlanSubstitutions } from './plan-substitutions.js';
import { PlanTesterImport } from './plan-tester-import.js';
import { ScopeTable } from './scope-table.js';

// Design-Spec §3.6 (Screen 3b, Testplan-Detail): Kopfzeile (Name + AKTIV-Chip) + 4 Kopfkarten
// (Teststufe, Zeitfenster, Scope-Modus, Aktiv) + "Speichern"-Aktion, die die editierbaren
// Felder per PATCH speichert. Der Scope-Modus wird zusätzlich beim Segment-Wechsel sofort
// gespeichert (Task-Brief: "Segment-Wechsel triggert plansApi.update"). Die Scope-&-
// Zuweisungstabelle darunter kommt mit Task 19 (`<ScopeTable>`) — hier nur ein Platzhalter-Slot.

function ScopeModeSegment({
  value,
  onChange,
}: {
  value: ScopeMode;
  onChange: (mode: ScopeMode) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex overflow-hidden rounded-chip border border-[var(--border)] text-xs">
      {SCOPE_MODE.map((mode) => (
        <button
          key={mode}
          type="button"
          onClick={() => onChange(mode)}
          className={`px-2 py-1 ${
            value === mode ? 'bg-brand-accent text-white' : 'text-text-muted hover:text-text-body'
          }`}
        >
          {t(`plans.scopeMode.${mode}`)}
        </button>
      ))}
    </div>
  );
}

export function PlanDetail() {
  const { t } = useTranslation();
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useScopedUser();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id ?? '';

  // #58: Testplan-Kopf bearbeiten (Teststufe, Zeitfenster, Scope-Modus, Aktiv-Schalter) ist
  // `plan.manage` (Backend erzwingt es zusätzlich). Ohne das Recht wird der Kopf rein lesend
  // gerendert: Werte als Text, kein Speichern-Button, KEIN Sofort-Speichern beim Scope-Modus.
  const canManage = user ? canInAnyScope(user, 'plan.manage') : false;
  // #131: Recht wie die Hand-Zuweisung im Plan (plan.manage, Offener Punkt 1); der Server erzwingt es
  // zusätzlich.
  const canImportTesters = canManage;

  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ['plan', projectId, planId],
    queryFn: () => plansApi.get(projectId, planId ?? ''),
    enabled: Boolean(projectId) && Boolean(planId),
  });

  const [teststufe, setTeststufe] = useState<Teststufe>(TESTSTUFE[0]);
  const [windowFrom, setWindowFrom] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [scopeMode, setScopeMode] = useState<ScopeMode>(SCOPE_MODE[2]);
  // Zwei Tabs statt einer langen Scroll-Seite (Nutzerwunsch): Scope-Konfiguration und
  // Testfortschritt. Die Kopfkarten bleiben über den Tabs in beiden sichtbar.
  const [activeTab, setActiveTab] = useState<'scope' | 'progress'>('scope');

  // Lokaler Formularstatus wird aus den Server-Daten befüllt — aber nur bei einem Wechsel der
  // Plan-IDENTITAET (test_plan_id), nicht bei jedem neuen Plan-OBJEKT (Branch-Review Befund 3).
  // Grund: `handleScopeModeChange` speichert den Scope-Modus sofort und invalidiert die Query; der
  // Refetch liefert (schon wegen `updated_at`) ein neues Objekt fuer DENSELBEN Plan. Haengt der
  // Effekt am Objekt `[plan]`, laeuft er bei diesem Refetch erneut und wirft gerade erst lokal
  // gesetzte, noch nicht gespeicherte Werte weg — etwa eine gerade getippte Teststufe oder ein
  // Datum: Nutzer aendert sie, wechselt den Scope-Modus (Sofortspeichern+Refetch), klickt
  // "Speichern" — der PATCH enthielte dann den SERVER-Stand. Mit der ID als Abhaengigkeit
  // bleibt die laufende Eingabe beim Refetch DESSELBEN Plans erhalten, ein echter Wechsel auf einen
  // ANDEREN Plan (andere test_plan_id) befuellt weiterhin korrekt neu.
  // `plan.teststufe` etc. im Effekt-Koerper sind ueber den fruehen `if (!plan) return` bereits
  // abgesichert — nur die Objekt-Identitaet soll den Effekt bewusst NICHT erneut ausloesen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusst nur test_plan_id, siehe Kommentar oben.
  useEffect(() => {
    if (!plan) return;
    setTeststufe(plan.teststufe);
    setWindowFrom(toDateInputValue(plan.window_from));
    setTargetDate(toDateInputValue(plan.target_date));
    setScopeMode(plan.scope_mode);
  }, [plan?.test_plan_id]);

  // #114 (A9): ein abgeschlossener Plan ist „lesbar, nicht bearbeitbar". Der Server antwortet auf
  // jeden Schreibversuch mit 409 plan_closed — die Oberfläche darf ihn deshalb gar nicht erst
  // anbieten, sonst wäre der Speichern-Knopf eine Falle. `canManage` bleibt davon unberührt: der
  // Lebenszyklus (Wieder öffnen) MUSS am abgeschlossenen Plan bedienbar bleiben.
  const canEdit = canManage && plan?.status !== 'abgeschlossen';

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['plan', projectId, planId] });
    void queryClient.invalidateQueries({ queryKey: ['plans', projectId] });
  };

  const save = useMutation({
    mutationFn: (body: TestPlanUpdateBody) => plansApi.update(projectId, planId ?? '', body),
    meta: { successToast: 'feedback.saved' },
    onSuccess: invalidate,
  });

  const handleSave = () => {
    save.mutate({
      teststufe,
      window_from: toIsoOrNull(windowFrom),
      target_date: toIsoOrNull(targetDate),
      scope_mode: scopeMode,
    });
  };

  const handleScopeModeChange = (mode: ScopeMode) => {
    setScopeMode(mode);
    save.mutate({ scope_mode: mode });
  };

  // 2. Abnahme, Befund 2: „einen Testplan löschen kann ich auch nicht, wenn er noch in Entwurf
  // ist." Die Route DELETE /plans/:planId gibt es samt Schutzriegel seit #85 — sie hatte nur
  // nirgends ein Bedienelement. Genau die Fehlerklasse aus CLAUDE.md („jedes Backend-Feld braucht
  // ein Bedienelement"), diesmal für eine ganze Operation.
  //
  // Sichtbar an `plan.manage` (der Server erzwingt es zusätzlich), NICHT an `canEdit`: ein
  // abgeschlossener Plan ohne Nachweise darf ebenfalls gelöscht werden — die Sperre für
  // abgeschlossene Pläne betrifft das Bearbeiten des Inhalts, nicht die Existenz des Plans.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => plansApi.remove(projectId, planId ?? ''),
    // errorToast: false — der 409 „hat Nachweise" ist keine Panne, sondern die Antwort auf die
    // Frage. Er wird darum benannt neben dem Knopf gezeigt statt als generischer roter Toast.
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onMutate: () => setDeleteError(null),
    onSuccess: () => {
      setConfirmDelete(false);
      void queryClient.invalidateQueries({ queryKey: ['plans', projectId] });
      // Der Plan existiert nicht mehr — auf seiner Detailseite zu bleiben würde nur „nicht
      // gefunden" zeigen.
      navigate('/plans');
    },
    onError: (err) => {
      setConfirmDelete(false);
      const evidence = parsePlanEvidence(err);
      setDeleteError(
        evidence?.error === 'plan_has_evidence'
          ? t('plans.deleteBlocked', {
              runs: evidence.run_count,
              signoffs: evidence.signoff_count,
            })
          : mapApiError(t, err),
      );
    },
  });

  if (projectLoading || planLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }

  if (!project) {
    return <div className="p-6 text-text-muted">{t('plans.noProject')}</div>;
  }

  if (!plan) {
    return <div className="p-6 text-text-muted">{t('plans.notFound')}</div>;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-text-primary">{plan.name}</h1>
          {/* #114 (A9): der Status steht im Kopf, nicht nur ein „AKTIV"-Chip, das bei Entwurf und
              Endzustand gleichermaßen fehlte. */}
          <PlanStatusChip status={plan.status} />
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={save.isPending}
              className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
            >
              {/* #84: nicht mehr das generische "Speichern" — der Knopf speichert die PLANDATEN
                  (Name/Teststufe/Zeitfenster/Scope-Modus/Aktiv), NICHT den Scope. Der Scope-Haken
                  speichert längst für sich (setScope in scope-table.tsx); die alte generische
                  Beschriftung ließ genau diese Verwechslung zu (#84: "muss ich hochscrollen, um
                  den Scope-Haken zu speichern?" — nein, mit diesem Knopf hier nie). */}
              {t('plans.saveHeader')}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={remove.isPending}
              className="rounded-btn border border-status-fail px-3 py-1.5 text-sm text-status-fail disabled:opacity-60"
            >
              {t('plans.delete')}
            </button>
          )}
        </div>
      </div>

      {deleteError && (
        <p role="alert" className="text-sm text-status-fail">
          {deleteError}
        </p>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t('plans.deleteTitle')}
          message={t('plans.deleteBody', { name: plan.name })}
          confirmLabel={t('plans.delete')}
          danger
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => remove.mutate()}
        />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1 rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <span className="text-xs text-text-muted">{t('plans.teststufe.label')}</span>
          {canEdit ? (
            <select
              value={teststufe}
              onChange={(e) => setTeststufe(e.target.value as Teststufe)}
              className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
            >
              {TESTSTUFE.map((stufe) => (
                <option key={stufe} value={stufe}>
                  {t(`plans.teststufe.${stufe}`)}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-text-primary">{t(`plans.teststufe.${teststufe}`)}</span>
          )}
        </div>

        <div className="flex flex-col gap-1 rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <span className="text-xs text-text-muted">{t('plans.window')}</span>
          {/* Start und Zieltermin sind fachlich EIN Zeitfenster: genau über diese Spanne
              rechnen Burndown-Ideallinie und Prognose. flex-wrap + basis-32, weil native
              Date-Inputs eine Mindestbreite haben und in schmalen Spalten sonst überlaufen.
              Sichtbare Beschriftungen statt nur aria-label (Branch-Review Befund 4): beide Felder
              zeigten dieselbe Datumsmaske und waren nur per Screenreader unterscheidbar — ein
              Vertauschen von Start/Zieltermin verzerrt Burndown und Prognose still. Wie im
              Anlege-Formular (plans-page.tsx). Ein umschließendes <label> erfüllt
              getByLabelText genauso wie zuvor das aria-label. */}
          {canEdit ? (
            <div className="flex flex-wrap gap-2">
              <label className="flex min-w-0 flex-1 basis-32 flex-col gap-1 text-xs text-text-muted">
                {t('plans.windowFrom')}
                <input
                  type="date"
                  value={windowFrom}
                  onChange={(e) => setWindowFrom(e.target.value)}
                  className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
                />
              </label>
              <label className="flex min-w-0 flex-1 basis-32 flex-col gap-1 text-xs text-text-muted">
                {t('plans.targetDate')}
                <input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
                />
              </label>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 text-sm text-text-primary">
              <span className="flex min-w-0 flex-1 basis-32 flex-col gap-1 text-xs text-text-muted">
                {t('plans.windowFrom')}
                <span className="text-sm text-text-primary">{windowFrom || '—'}</span>
              </span>
              <span className="flex min-w-0 flex-1 basis-32 flex-col gap-1 text-xs text-text-muted">
                {t('plans.targetDate')}
                <span className="text-sm text-text-primary">{targetDate || '—'}</span>
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <span className="text-xs text-text-muted">{t('plans.scopeMode.label')}</span>
          {canEdit ? (
            <ScopeModeSegment value={scopeMode} onChange={handleScopeModeChange} />
          ) : (
            <span className="text-sm text-text-primary">{t(`plans.scopeMode.${scopeMode}`)}</span>
          )}
          {/* #127: Der Modus hat eine Konsequenz für die Tester — die Zeile sagt sie, damit niemand
              den Scope-Schalter für reine Beschriftung hält. */}
          <span data-testid="plan-scope-hint" className="text-xs text-text-muted">
            {t(
              chainOrderEnforced(scopeMode)
                ? 'plans.scopeMode.hint.chained'
                : 'plans.scopeMode.hint.processes',
            )}
          </span>
        </div>

        <div className="flex flex-col gap-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <span className="text-xs text-text-muted">{t('plans.statusLabel')}</span>
          {/* #114 (A9): aus dem Aktiv-Häkchen wird der Lebenszyklus. Das Häkchen kannte nur „an/aus"
              und konnte den Endzustand gar nicht ausdrücken; außerdem lief es über den
              Speichern-Knopf des Plan-Kopfes mit und hätte damit alle Bedingungen des Übergangs
              (höchstens ein aktiver Plan, offene Bestätigungen) umgangen. */}
          <div className="flex items-center gap-2">
            <PlanStatusChip status={plan.status} />
          </div>
          <p className="text-[11px] leading-snug text-text-muted">
            {t(`plans.statusHint.${plan.status}`)}
          </p>
          {/* Der Abschluss-/Wiedereröffnungs-Knopf hängt am selben Recht wie der Plan-Kopf
              (`plan.manage`); der Server erzwingt es zusätzlich. */}
          {canManage && <PlanStatusActions projectId={projectId} plan={plan} />}
          {plan.status === 'abgeschlossen' && plan.closed_note && (
            // Der Grund gehört sichtbar an den Plan: er ist die Antwort auf „warum wurde trotz
            // offener Abnahmen abgeschlossen?" und stünde sonst nur im Audit-Log.
            <p className="text-[11px] leading-snug text-text-muted">
              {t('plans.closedNote', { note: plan.closed_note })}
            </p>
          )}
        </div>
      </div>

      {/* Tab-Leiste: teilt die vorher sehr lange Seite in Scope-Konfiguration und Testfortschritt.
          Muster wie die Katalog-Tabs (catalog-page.tsx). */}
      <div className="flex items-center gap-4 border-b border-[var(--border)] text-sm">
        {(['scope', 'progress'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`border-b-2 pb-2 ${
              activeTab === tab
                ? 'border-brand-accent text-brand-accent'
                : 'border-transparent text-text-muted hover:text-text-body'
            }`}
          >
            {t(`plans.tab.${tab}`)}
          </button>
        ))}
      </div>

      {/* Je Abschnitt eine eigene Boundary: der Auslöser des Weiße-Seite-Bugs saß im
          Fortschritts-Abschnitt, riss aber den Rest mit. Getrennt gefangen bleibt der Rest
          bedienbar. */}
      {activeTab === 'scope' ? (
        <>
          {/* #50 (PLAN-5): Vertretungen an den ANFANG der Scope-Konfiguration gezogen (Nutzerwunsch:
              „weiter nach oben"), damit man sie nicht erst unter der langen Scope-Tabelle sucht.
              Projektweit, nicht plan-spezifisch; nur mit plan.manage sichtbar (sonst rendert die
              Komponente null). */}
          <ErrorBoundary>
            <PlanSubstitutions projectId={projectId} />
          </ErrorBoundary>
          {canImportTesters && (
            <ErrorBoundary>
              <PlanTesterImport
                projectId={projectId}
                planId={plan.test_plan_id}
                planName={plan.name}
                planClosed={plan.status === 'abgeschlossen'}
              />
            </ErrorBoundary>
          )}
          <ErrorBoundary>
            <ScopeTable projectId={projectId} planId={plan.test_plan_id} />
          </ErrorBoundary>
        </>
      ) : (
        <>
          {/* Abnahmestand + Fortschritt: teilen sich den Query-Key ['signoffs', projectId, planId]
              mit der Sign-off-Seite, sind hier also ohne Zutun aktuell. */}
          <ErrorBoundary>
            <PlanSignoffStatus projectId={projectId} planId={plan.test_plan_id} />
          </ErrorBoundary>
          <ErrorBoundary>
            <PlanProgressSection projectId={projectId} planId={plan.test_plan_id} />
          </ErrorBoundary>
        </>
      )}
    </div>
  );
}
